'use client';

import { Dispatch, SetStateAction, useRef, useState } from 'react';

/**
 * Custom hook that combines useState and useRef to keep a mutable reference of the state.
 * @function useStateRef
 * @template T - The type of the state value.
 * @param {T} initialValue - The initial value of the state.
 * @returns {[T, Dispatch<SetStateAction<T>>, React.MutableRefObject<T>]} An array containing the state value, the state setter function, and the mutable ref object.
 */
export const useStateRef = <T>(
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>, React.MutableRefObject<T>] => {
  const [value, setInternalValue] = useState<T>(initialValue);

  const ref = useRef(value);

  const setValue = (value: T) => {
    ref.current = value;
    setInternalValue(value);
  };

  return [value, setValue as Dispatch<SetStateAction<T>>, ref];
};
